import React, { useEffect, useState } from 'react'
import LoginFrame, { LoginFrameChildrenProps } from './shared/LoginFrame'
import LoadingButton from './shared/LoadingButton'
import { eventHandler } from '../types.d'
import { preventDefault, preventDefaultAndValidate } from '../helpers'
import { InjectedFrameProps, withFrameProps } from '../context/withFrameProps'
import { WebAuthnAbortService } from '@simplewebauthn/browser'
import { useStartSignin, useAppDispatch } from '../hooks/useAuthActions'
import { useAppSelector, selectAuthConfig } from '../store/store'
import { signinWithAnonymousPasskey, startSignup } from '../store'

type PureStartContentsProps = {
  email: string
  setEmail: (email: string) => void
  handleSignup: eventHandler
  onContinue: (token: Promise<string>, email: string) => void
} & LoginFrameChildrenProps

const PureStartContents = ({
  recaptchaPromise,
  recaptchaLoading,
  email,
  setEmail,
  handleSignup,
  onContinue,
}: PureStartContentsProps) => {
  const handleSubmit = async (e: React.FormEvent) => {
    onContinue(recaptchaPromise(), email)
  }

  return (
    <>
      <div>
        <label
          htmlFor="email"
          className="block mb-2 text-sm font-medium text-gray-900"
        >
          Your email
        </label>
        <input
          autoFocus
          type="email"
          name="email"
          id="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="username webauthn"
          className="bg-gray-50 border border-gray-300 text-gray-900 rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5"
          placeholder="name@company.com"
          required
        />
      </div>
      <LoadingButton
        onClick={preventDefaultAndValidate(handleSubmit)}
        loading={recaptchaLoading}
        type="submit"
        className="w-full text-white bg-slate-600 hover:bg-slate-700 focus:ring-4 focus:outline-none focus:ring-primary-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center"
      >
        Continue
      </LoadingButton>
    </>
  )
}

type PureStartProps = {
  onContinue: PureStartContentsProps['onContinue']
  onSignup: (email: string) => void
  recaptchaSiteKey?: string | null
  initialEmail?: string
} & InjectedFrameProps

export const PureStart = ({
  onContinue,
  onSignup,
  recaptchaSiteKey = null,
  logoUrl = null,
  initialEmail = '',
  ...props
}: PureStartProps) => {
  const [email, setEmail] = useState(initialEmail)

  const handleSignup = (e: React.MouseEvent<HTMLAnchorElement>) => {
    onSignup(email)
  }

  const errorHandler = (error: string) => {
    if (error === 'SIGNUP_PROMPT') {
      return (
        <>
          We don't recognise that email. Would you like to{' '}
          <a
            href="#"
            onClick={preventDefaultAndValidate(handleSignup)}
            className="font-bold hover:underline"
          >
            Create an Account
          </a>
          ?
        </>
      )
    }

    return error
  }

  return (
    <LoginFrame {...{ ...props, errorHandler, recaptchaSiteKey, logoUrl }}>
      <PureStartContents
        {...{ email, setEmail, onSignup, handleSignup, onContinue }}
      />
    </LoginFrame>
  )
}

const BoundStart = withFrameProps(PureStart, { hasPreviousStep: false })

export const Start = () => {
  const dispatch = useAppDispatch()
  const startSignin = useStartSignin()
  const config = useAppSelector(selectAuthConfig)

  useEffect(() => {
    // Auto-submit when captcha bypass is enabled
    if (config.captchaBypass && config.loginHint) {
      console.log('Auto-submitting due to captcha bypass')
      startSignin(Promise.resolve(null), config.loginHint)
      return
    }

    // Skip anonymous passkey if we have a login hint
    if (config.loginHint) {
      console.log('Skipping anonymous passkey signin due to login hint')
      return
    }

    let isCancelled = false
    let lastCallTime = 0

    const tryAnonymousSignin = () => {
      if (isCancelled) return

      const now = Date.now()
      if (lastCallTime > 0 && now - lastCallTime < 500) {
        console.log('Anonymous passkey signin looping too quickly, aborting')
        return
      }
      lastCallTime = now

      dispatch(signinWithAnonymousPasskey())
        .unwrap()
        .then((result) => {
          if (isCancelled) return

          // null is terminal - do nothing, stop trying
          if (result === null) {
            console.log('Anonymous passkey signin terminated (null result)')
          }
          // Success case - also terminal, authentication flow continues
        })
        .catch((error) => {
          if (isCancelled) return

          // If error is terminal (e.g., needs to accept T&Cs), don't retry
          if (error?.terminal) {
            console.log(
              'Anonymous passkey signin terminal error, not retrying:',
              error,
            )
            return
          }

          // Error case - retry after delay
          console.log('Anonymous passkey signin error, retrying:', error)
          tryAnonymousSignin()
        })
    }

    tryAnonymousSignin()

    return () => {
      isCancelled = true
      console.log('Cancelling WebAuthn ceremony')
      WebAuthnAbortService.cancelCeremony()
    }
  }, [config.loginHint, config.captchaBypass])

  const onSignup = (email: string) => {
    dispatch(startSignup(email))
  }

  return (
    <BoundStart
      onContinue={startSignin}
      {...{ onSignup }}
      initialEmail={config.loginHint || undefined}
    />
  )
}

export default Start
